import { PersonalizationTagEnum } from '@Discover/Targeting/enums/personalization-tag.enum';

import { OutletModel } from './outlet.model';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { PersonalizationSourceTypeEnum } from '@api/distributions/enum/personalization-source-type.enum';
import { PersonalizationApiModel, PersonalizationResultApiModel } from '@api/distributions/model/distribution-personalization.api.model';
import { EmploymentModel } from './employment.model';


export class RecipientPersonalization {

    recipient: OutletModel | ContactModel;
    personalization: Personalization;

    constructor(apiModel?: PersonalizationResultApiModel) {
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        }
    }

    constructFromApiModel(apiModel: PersonalizationResultApiModel) {
        this.recipient = apiModel.entityType === EntityType.CONTACT
            ? new ContactModel(apiModel.contactInfo) : new OutletModel(apiModel.outletInfo);

        if (apiModel.entityType === EntityType.CONTACT) {
            this.recipient = new ContactModel(apiModel.contactInfo);

            this.recipient.selectedEmployment = new EmploymentModel(apiModel.employments?.pop());
        } else {
            this.recipient = new OutletModel(apiModel.outletInfo);
        }

        this.personalization = new Personalization(apiModel.personalizations);
    }

    /**
     * @deprecated  - Not used.
     * System is readonly and all other source types have separate endpoints for saving
     */
    toApiModel() {}
}



export class Personalization {
    tagType: PersonalizationTagEnum;

    selectedSource: PersonalizationSourceTypeEnum.CUSTOM | PersonalizationSourceTypeEnum.SYSTEM;

    system: string; // journodex data (readonly)
    fallback: string; // fallback value
    custom: string; // custom distribution value (sometimes contains profile value)
    profile: string; // recipient profile private data

    // special case, needed for stat calculation differentials
    previousCustom: string;

    constructor(apiModel?: PersonalizationApiModel) {
        if (apiModel) {
            this.constructFromApiModel(apiModel);
        } else {
            this.constructDefault();
        }
    }

    constructFromApiModel(apiModel: PersonalizationApiModel) {
        this.tagType = apiModel.tagType;

        this.system = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.SYSTEM)?.value || '';
        this.fallback = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.FALLBACK)?.value || '';
        this.custom = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.CUSTOM)?.value || '';
        this.profile = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.ENTITY_VALUE)?.value || '';

        const profileSelection = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.ENTITY_VALUE);
        const customSelection = apiModel.selections.find(x => x.type === PersonalizationSourceTypeEnum.CUSTOM);

        const selectedValue = apiModel.selections.find(x => x.isSelected);

        if (selectedValue) {
            if ([PersonalizationSourceTypeEnum.SYSTEM, PersonalizationSourceTypeEnum.FALLBACK].includes(selectedValue.type)) {
                this.selectedSource = PersonalizationSourceTypeEnum.SYSTEM;
            } else {
                this.selectedSource = PersonalizationSourceTypeEnum.CUSTOM;
                if (profileSelection && !customSelection) {
                    this.custom = this.profile;
                }
            }
        } else if (profileSelection && !customSelection) {
            // beginning state: when profile exists and a custom doesn't, show profile in custom field
            this.selectedSource = PersonalizationSourceTypeEnum.CUSTOM;
            this.custom = this.profile;
        } else {
            this.selectedSource = PersonalizationSourceTypeEnum.SYSTEM;
        }

        this.previousCustom = this.custom;
    }

    constructDefault() {
        this.tagType = null;
        this.selectedSource = PersonalizationSourceTypeEnum.CUSTOM;
        this.system = '';
        this.fallback = '';
        this.custom = '';
        this.profile = '';
        this.previousCustom = '';
    }

    /**
     * @deprecated  - Not used.
     * System is readonly and all other source types have separate endpoints for saving
     */
    toApiModel() {}

    clone(): Personalization {
        const clone = new Personalization();

        clone.tagType = this.tagType;
        clone.selectedSource = this.selectedSource;
        clone.system = this.system;
        clone.fallback = this.fallback;
        clone.profile = this.profile;
        clone.custom = this.custom;
        clone.previousCustom = this.custom;

        return clone;
    }

    equals(other: Personalization): boolean {
        if (this.tagType !== other.tagType) {
            return false;
        }

        if (this.selectedSource !== other.selectedSource) {
            return false;
        }

        if (this.system !== other.system) {
            return false;
        }

        if (this.fallback !== other.fallback) {
            return false;
        }

        if (this.custom !== other.custom) {
            return false;
        }

        if (this.previousCustom !== other.previousCustom) {
            return false;
        }

        if (this.profile !== other.profile) {
            return false;
        }

        return true;
    }
}
